import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import FormField from "components/molecules/FormField/FormField";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import errorToast from "helpers/errorToast";
import getLabel from "helpers/getLabel";
import Alert from "components/molecules/Alert/Alert";
import { isValidEmail } from "helpers/validationHelpers";
import { addContact, deleteContact, getContact, updateContact } from "api";
import testIds from "constants/testIds";

const EditContactList = function ({
  showContactDialog,
  closeContactDialog,
  selectedId,
  setSelectedId,
  refetch,
  allowPaymentLocation,
}) {
  const intl = useIntl();
  const methods = useForm({ shouldFocusError: true });
  const { setValue, handleSubmit, reset } = methods;

  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getContactDetails = () => {
    setIsLoading(true);
    getContact(selectedId)
      .then((res) => setSelectedContact(res.data))
      .catch((err) => errorToast(err));
  };

  const prefillEditData = () => {
    setValue("company", selectedContact.company);
    setValue("email", selectedContact.email);
    setValue("name", selectedContact.full_name);
    setValue("phone", selectedContact.phone);
    setValue("location", selectedContact.payment_location.reference_number);
  };

  useEffect(() => {
    if (selectedId) getContactDetails();
  }, [selectedId]);

  useEffect(() => {
    if (selectedContact) {
      prefillEditData();
      setIsLoading(false);
    } else {
      reset({
        company: "",
        email: "",
        name: "",
        phone: "",
        location: "",
      });
    }
  }, [selectedContact]);

  const handleCloseContactDialog = () => {
    reset({
      company: "",
      email: "",
      name: "",
      phone: "",
      location: "",
    });
    setShowConfirmDelete(false);
    setSelectedId(null);
    setSelectedContact(null);
    setIsSubmitting(false);
    setIsDeleting(false);
    closeContactDialog();
  };

  const onSuccess = () => {
    handleCloseContactDialog();
    refetch();
    toast(
      intl.formatMessage({
        id: selectedId ? "settings.contact-list.updateSuccess" : "settings.contact-list.addSuccess",
        defaultMessage: selectedId ? "Contact successfully updated" : "Contact successfully added",
      }),
      {
        type: "success",
      },
    );
  };

  const onDelete = () => {
    setIsDeleting(true);
    deleteContact(selectedId)
      .then(() => {
        handleCloseContactDialog();
        refetch(true);
        toast(
          intl.formatMessage({
            id: "settings.contact-list.deleteSuccess",
            defaultMessage: "Contact successfully deleted",
          }),
          {
            type: "success",
          },
        );
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsDeleting(false));
  };

  const onSubmit = (data) => {
    setIsSubmitting(true);
    const payload = {
      company: data.company,
      email: data.email,
      full_name: data.name,
      phone: data.phone,
      ...(allowPaymentLocation && { payment_location: data.location }),
    };
    if (selectedId)
      updateContact(selectedId, payload)
        .then(() => onSuccess())
        .catch((err) => {
          errorToast(err);
        })
        .finally(() => setIsSubmitting(false));
    else
      addContact(payload)
        .then(() => onSuccess())
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
  };

  const formatLocations = () => {
    const { locations } = JSON.parse(localStorage.getItem("cargobase_userpermissions")).payment_location;
    const formattedLocations = locations
      .filter((location) => location.is_active)
      .map((location) => ({
        value: location.value,
        label: location.label,
      }));
    return formattedLocations;
  };

  return (
    <Dialog
      show={showContactDialog}
      title={intl.formatMessage({
        id: "settings.your-details.contactDetails",
        defaultMessage: "Contact Details",
      })}
      onHide={handleCloseContactDialog}
      isLoading={isLoading}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {allowPaymentLocation && (
            <Row className="m-0">
              <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
                <div className="required">
                  <FormattedMessage
                    id="general.paymentLocation"
                    values={{ paymentLocation: getLabel("Payment location", "capitalize") }}
                    defaultMessage="Payment location"
                  />
                </div>
              </Col>
              <Col sm={8} className="pr-md-0">
                <Dropdown
                  name="location"
                  eventContext="Edit Contact Dialog"
                  placeholder={intl.formatMessage({
                    id: "general.pleaseSelect",
                    defaultMessage: "Please Select",
                  })}
                  options={formatLocations()}
                  useHookForm
                  required
                />
              </Col>
            </Row>
          )}

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="general.name" defaultMessage="Name" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "general.name",
                    defaultMessage: "Name",
                  })}
                  type="text"
                  required
                  field_id="name"
                  eventContext="Edit Contact Dialog"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="audit-trail.company" defaultMessage="Company" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "audit-trail.company",
                    defaultMessage: "Company",
                  })}
                  type="text"
                  required
                  field_id="company"
                  eventContext="Edit Contact Dialog"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="general.email" defaultMessage="Email" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "general.email",
                    defaultMessage: "Email",
                  })}
                  validations={{
                    validate: {
                      validEmail: (v) =>
                        isValidEmail(v) ||
                        intl.formatMessage({
                          id: "general.invalidEmail",
                          defaultMessage: "Invalid Email",
                        }),
                    },
                  }}
                  type="text"
                  required
                  field_id="email"
                  eventContext="Edit Contact Dialog"
                />
              </div>
            </Col>
          </Row>

          <Row className="m-0 mt-3">
            <Col className="d-flex pl-0 align-items-center mb-10 mb-md-0" sm={4}>
              <div className="required">
                <FormattedMessage id="new-request.schedule.pickupContactPhone" defaultMessage="Phone" />
              </div>
            </Col>
            <Col sm={8} className="pr-md-0">
              <div className="input-element">
                <FormField
                  placeholder={intl.formatMessage({
                    id: "new-request.schedule.pickupContactPhone",
                    defaultMessage: "Phone",
                  })}
                  type="text"
                  required
                  field_id="phone"
                  eventContext="Edit Contact Dialog"
                />
              </div>
            </Col>
          </Row>

          <Row className="mt-3" style={{ height: "45px" }}>
            <Col sm={4} />
            {!showConfirmDelete && (
              <Col sm={12} className="d-flex justify-content-between mt-3">
                <Button
                  variant="outline-primary"
                  label={intl.formatMessage({
                    id: "settings.contact-list.discardChanges",
                    defaultMessage: "Discard changes",
                  })}
                  onClick={() => handleCloseContactDialog()}
                />
                <div className="d-flex">
                  {selectedId && (
                    <Button
                      size="md"
                      label={intl.formatMessage({
                        id: "settings.contact-list.deleteContact",
                        defaultMessage: "Delete contact",
                      })}
                      variant="danger"
                      className="mr-3"
                      onClick={() => setShowConfirmDelete(true)}
                      data-testid={testIds.CONTACT_LIST_DELETE_BUTTON}
                    />
                  )}
                  <Button className="ml-auto" variant="success" type="submit" data-testid={testIds.CONTACT_LIST_SAVE_BUTTON}>
                    <FormattedMessage
                      id={isSubmitting ? "general.saving" : "general.save"}
                      defaultMessage={isSubmitting ? "Saving" : "Save"}
                    />
                  </Button>
                </div>
              </Col>
            )}
            {showConfirmDelete && (
              <div className="d-flex align-items-center justify-content-between">
                <Alert variant="warning">
                  <FormattedMessage
                    id="settings.contact-list.deleteContactConfirm"
                    defaultMessage="Are you sure you want to delete this contact?"
                  />
                </Alert>
                <span className="d-flex">
                  <Button
                    label={intl.formatMessage({
                      id: "general.cancel",
                      defaultMessage: "Cancel",
                    })}
                    onClick={() => setShowConfirmDelete(false)}
                    variant="outline-primary"
                  />
                  <Button
                    className="ml-3"
                    label={intl.formatMessage({
                      id: "general.delete",
                      defaultMessage: "Delete",
                    })}
                    isLoading={isDeleting}
                    variant="danger"
                    onClick={onDelete}
                  />
                </span>
              </div>
            )}
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
};

EditContactList.propTypes = {
  allowPaymentLocation: PropTypes.bool.isRequired,
  closeContactDialog: PropTypes.func.isRequired,
  showContactDialog: PropTypes.bool.isRequired,
  selectedId: PropTypes.number,
  setSelectedId: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

EditContactList.defaultProps = {
  selectedId: null,
};

export default EditContactList;
